import { 
  useState, 
  useRef, 
  useEffect, 
  ReactNode, 
  useCallback,
  createContext,
  useContext
} from 'react'
import { cn } from '@/lib/utils'

interface DropdownContextType {
  isOpen: boolean
  toggle: () => void
  close: () => void
}

const DropdownContext = createContext<DropdownContextType | null>(null)

const useDropdown = () => {
  const context = useContext(DropdownContext)
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown')
  }
  return context
}

interface DropdownProps {
  children: ReactNode
  className?: string
}

interface DropdownTriggerProps {
  children: ReactNode
  className?: string
}

interface DropdownContentProps {
  children: ReactNode
  className?: string
  align?: 'left' | 'right' | 'center'
  sideOffset?: number
}

interface DropdownItemProps {
  children: ReactNode
  onClick?: () => void
  className?: string
  disabled?: boolean
  variant?: 'default' | 'danger'
}

interface DropdownSeparatorProps {
  className?: string
}

const Dropdown = ({ children, className }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        close()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [isOpen, close])

  return (
    <DropdownContext.Provider value={{ isOpen, toggle, close }}>
      <div ref={dropdownRef} className={cn('relative inline-block', className)}>
        {children}
      </div>
    </DropdownContext.Provider>
  )
}

const DropdownTrigger = ({ children, className }: DropdownTriggerProps) => {
  const { toggle, isOpen } = useDropdown()

  return (
    <button
      onClick={toggle}
      aria-expanded={isOpen}
      aria-haspopup={true}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        className
      )}
    >
      {children}
    </button>
  )
}

const DropdownContent = ({ 
  children, 
  className, 
  align = 'right',
  sideOffset = 8 
}: DropdownContentProps) => {
  const { isOpen } = useDropdown()

  if (!isOpen) return null

  return (
    <div
      className={cn(
        'absolute z-50 min-w-[12rem] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800',
        'animate-in fade-in-0 zoom-in-95',
        {
          'right-0': align === 'right',
          'left-0': align === 'left',
          'left-1/2 -translate-x-1/2': align === 'center',
        },
        className
      )}
      style={{ top: `calc(100% + ${sideOffset}px)` }}
      role="menu"
      aria-orientation="vertical"
    >
      <div className="p-1">
        {children}
      </div>
    </div>
  )
}

const DropdownItem = ({ 
  children, 
  onClick, 
  className, 
  disabled = false,
  variant = 'default'
}: DropdownItemProps) => {
  const { close } = useDropdown()

  const handleClick = useCallback(() => {
    if (!disabled && onClick) {
      onClick()
      close()
    }
  }, [disabled, onClick, close])

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      role="menuitem"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
        'focus:bg-gray-100 focus:text-gray-900 dark:focus:bg-gray-700 dark:focus:text-gray-100',
        {
          'text-gray-900 dark:text-gray-100': variant === 'default' && !disabled,
          'text-danger hover:bg-danger/10 focus:bg-danger/10 focus:text-danger': variant === 'danger' && !disabled,
          'text-gray-400 cursor-not-allowed dark:text-gray-600': disabled,
          'hover:bg-gray-100 dark:hover:bg-gray-700': variant === 'default' && !disabled,
        },
        className
      )}
    >
      {children}
    </button>
  )
}

const DropdownSeparator = ({ className }: DropdownSeparatorProps) => (
  <div 
    className={cn('my-1 h-px bg-gray-200 dark:bg-gray-700', className)} 
    role="separator" 
  />
)

// Compound component exports
Dropdown.Trigger = DropdownTrigger
Dropdown.Content = DropdownContent
Dropdown.Item = DropdownItem
Dropdown.Separator = DropdownSeparator

export default Dropdown 